import React, { useCallback, useState } from 'react';
import Message from '@app/component/message/Message';
import Utilities from '@util/Utilities';
import _isEmpty from 'lodash/isEmpty';
import ParityDailySnapshot from '@app/model/parity/ParityDailySnapshot';
import DataProvider, {
	providersWithFlightSupport,
	providersWithNoFlightSupport,
} from '@app/model/_common/DataProvider';
import ParityDailyProviderSnapshot from '@app/model/parity/ParityDailyProviderSnapshot';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import moment from 'moment';
import { findProviderData } from '@app/component/provider/FindProviderData';
import {
	useMarketStateParityForPeriod,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateParityForPeriod';
import {
	useOtaPricesForPeriod,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useOtaPricesForPeriod';
import { OtaPriceStateSnapshot } from '@app/model/parity/OtaPriceStateSnapshot';
import { Tooltip } from 'react-tooltip';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import ParityFilterInput from '@app/model/parity/input/ParityFilterInput';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { AirportCodeKey } from '@app/model/market_state/AirportCode';
import ParitySnapshot from '@app/model/parity/ParitySnapshot';
import MarketStateItem from '@app/model/market_state/MarketStateItem';
import MarketStateService from '@app/model/market_state/service/MarketStateService';


const DashboardParityDetailsView: React.FunctionComponent<{ filter: ParityFilterInput }> = ({ filter }) => {

	const paritySnapshot = useMarketStateParityForPeriod();
	const { companyHotel } = useSelectedCompanyHotel();
	const otaPriceStateSnapshot = useOtaPricesForPeriod();

	if (!!paritySnapshot && !_isEmpty(paritySnapshot.dailyItems)) {

		const hasAirportCode = !!filter.airportCode;
		const hasMultiAirportCodes = hasAirportCode && !_isEmpty(filter.compareAirportCodes);

		const nonFlightProviders = [...new Set(companyHotel!.parsingSettings?.filter(s => s.state === 'ACTIVE'
			&& providersWithNoFlightSupport.has(s.variables.provider)
		).map(s => s.variables.provider) || [])].sort();

		const flightProviders = [...new Set(companyHotel!.parsingSettings?.filter(s => s.state === 'ACTIVE'
			&& providersWithFlightSupport.has(s.variables.provider) && hasAirportCode
		).map(s => s.variables.provider) || [])].sort();


		return <div className="w-full shadow ring-1 ring-black ring-opacity-5">
			<div className="overflow-x-auto overflow-y-scroll max-h-[70dvh]">
				<div className="inline-block min-w-full align-middle">
					<table className="min-w-full divide-y divide-gray-300">
						<thead className="sticky-table-header">
						<tr>
							{hasMultiAirportCodes &&
								<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:pl-3" />}
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:pl-3">Date</th>
							<RenderProviderHeader provider={DataProvider.BRAND_COM} />
							{nonFlightProviders.map(p => <RenderProviderHeader key={p} provider={p} />)}

							{!otaPriceStateSnapshot.isEmpty &&
								<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
									Lowest rate on metasearch
								</th>}
							{flightProviders.map(p => <RenderProviderHeader key={p} provider={p} isFlightProvider />)}

						</tr>
						</thead>
						<tbody className="bg-white">
						{paritySnapshot.dailyItems.filter(dailyItem => !dailyItem.airportCode || dailyItem.airportCode == filter.airportCode)
							.map(dailyItem => <RenderParityDailyRow key={dailyItem.statDate}
																											companyHotel={companyHotel}
																											filter={filter}
																											paritySnapshot={paritySnapshot}
																											dailyItem={dailyItem}
																											otaPriceStateSnapshot={otaPriceStateSnapshot}
																											hasMultiAirportCodes={hasMultiAirportCodes}
																											nonFlightProviders={nonFlightProviders}
																											flightProviders={flightProviders} />)}
						</tbody>
					</table>
				</div>
			</div>
		</div>;
	} else if (!!paritySnapshot && _isEmpty(paritySnapshot.dailyItems)) return <Message state="info">
		Parity report is empty. Please refresh results!
	</Message>;
	else return <Message state="error">
			Parity report could not be loaded. Please try again later!
		</Message>;
};

const RenderParityDailyRow: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	filter: ParityFilterInput,
	paritySnapshot: ParitySnapshot,
	dailyItem: ParityDailySnapshot,
	otaPriceStateSnapshot: OtaPriceStateSnapshot,
	hasMultiAirportCodes: boolean,
	nonFlightProviders: DataProvider[],
	flightProviders: DataProvider[]
}> = ({
				companyHotel,
				filter,
				paritySnapshot,
				dailyItem,
				otaPriceStateSnapshot,
				hasMultiAirportCodes,
				nonFlightProviders,
				flightProviders,
			}) => {

	const [isOpen, setIsOpen] = useState(false);

	const today = new Date();
	const findProviderItem = (dailyItem: ParityDailySnapshot, provider: DataProvider) => dailyItem.providerItems.find(d => d.provider === provider);
	const brandItem = findProviderItem(dailyItem, DataProvider.BRAND_COM);

	const diff = moment(dailyItem.statDate).diff(today, 'days');
	const isPast = diff < 0;
	const isToday = diff === 0;

	return <>
		<tr key={dailyItem.statDate}
				className={Utilities.classNames(isToday ? 'bg-blue-50' : '', isPast ? '!bg-gray-50/80' : '')}>
			{hasMultiAirportCodes &&
				<td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900 sm:pl-3">
					<a className="text-gray-400 hover:text-gray-700 hover:cursor-pointer" title="View details"
						 onClick={() => setIsOpen(!isOpen)}>
						{isOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
					</a>
				</td>}
			<td
				className={`whitespace-nowrap px-3 py-4 text-sm font-medium ${isPast ? 'text-gray-900/20' : 'text-gray-900'} sm:pl-3 border-r-[1px] border-gray-200`}>
				{Utilities.formatDateString(dailyItem.statDate, 'ddd DD/MM', false)}
			</td>
			<td className={`whitespace-nowrap px-3 py-4 text-sm ${isPast ? 'text-gray-900/20' : 'text-gray-900'}`}>
				{(!brandItem || !brandItem.item) &&
					<RenderProviderPrice providerItem={brandItem} />}
				{!!brandItem && !!brandItem.item &&
					<a className="cursor-pointer" onClick={() => openMarketStateItemUrl(brandItem.item)}>
						<div className="flex gap-1 items-center hover:text-blue-600">
							<RenderProviderPrice providerItem={brandItem} />
							<ArrowTopRightOnSquareIcon className="h-3 w-3" />
						</div>
					</a>}
			</td>
			{nonFlightProviders.map(p => <RenderProviderItem companyHotel={companyHotel} provider={p} key={p}
																											 brandItem={brandItem} dailyItem={dailyItem}
																											 isPast={isPast} />)}
			<RenderOtaPriceDailyState companyHotel={companyHotel} otaPriceStateSnapshot={otaPriceStateSnapshot}
																brandItem={brandItem}
																statDate={dailyItem.statDate}
																isPast={isPast} />
			{flightProviders.map(p => <RenderProviderItem key={p}
																										companyHotel={companyHotel}
																										provider={p}
																										brandItem={brandItem}
																										dailyItem={dailyItem}
																										isPast={isPast}
																										isFlightProvider />)}
		</tr>
		{isOpen && filter.compareAirportCodes.map(ac => <RenderAirportComparisonRow key={ac}
																																								companyHotel={companyHotel}
																																								paritySnapshot={paritySnapshot}
																																								statDate={dailyItem.statDate}
																																								brandItem={brandItem}
																																								airportCode={ac}
																																								nonFlightProviders={nonFlightProviders}
																																								flightProviders={flightProviders}
																																								isPast={isPast} />)}
	</>;
};

const RenderAirportComparisonRow: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	paritySnapshot: ParitySnapshot,
	statDate: string,
	brandItem: ParityDailyProviderSnapshot | undefined,
	airportCode: AirportCodeKey,
	nonFlightProviders: DataProvider[],
	flightProviders: DataProvider[],
	isPast: boolean
}> = ({ companyHotel, paritySnapshot, statDate, brandItem, airportCode, nonFlightProviders, flightProviders, isPast }) => {

	const dailyItem = paritySnapshot.dailyItems.find(d => d.statDate == statDate && d.airportCode == airportCode);
	console.log(dailyItem)

	return <tr className="!bg-gray-50">
		<td className="whitespace-nowrap px-3 py-3 text-sm text-gray-400 sm:pl-3"></td>
		<td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 sm:pl-3 border-r-[1px] border-gray-200"><span
			className="block text-xs font-medium text-gray-400">{airportCode}</span></td>
		{nonFlightProviders.map((_, index) => <td key={index}
																							className="whitespace-nowrap px-3 py-3 text-sm text-gray-500" />)}
		<td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 border-r-[1px] border-gray-200"/>
		<td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 border-r-[1px] border-gray-200"/>
		{!!dailyItem && flightProviders.map(p => <RenderProviderItem key={p}
																									companyHotel={companyHotel}
																									provider={p}
																									brandItem={brandItem}
																									dailyItem={dailyItem}
																									isPast={isPast}
																									isFlightProvider />)}
		{!dailyItem && flightProviders.map((_, index) =><td key={index} className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 border-r-[1px] border-gray-200"/>)}
	</tr>;
};

const RenderProviderItem: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	provider: DataProvider,
	dailyItem: ParityDailySnapshot,
	brandItem?: ParityDailyProviderSnapshot,
	isFlightProvider?: boolean,
	isPast: boolean
}> = ({ companyHotel, provider, dailyItem, brandItem, isFlightProvider, isPast }) => {

	const providerItem = dailyItem.providerItems.find(d => d.provider === provider);

	let isCheaper = false;
	if (!!providerItem && !!brandItem) {
		isCheaper = providerItem.isAvailable && brandItem.isAvailable && parseInt(providerItem.item.price.amount.toString()) * (1 + ((companyHotel.settings?.calendar?.parityDifferencePercentage ?? 0) / 100)) < parseInt(brandItem.item.price.amount.toString());
	}

	let textClass = isCheaper ? 'text-amber-500' : 'text-gray-500';
	textClass = isPast ? `${textClass}/20` : textClass;

	return <td className={`whitespace-nowrap px-3 py-4 text-sm ${textClass}`}>

		{(!providerItem || !providerItem.item) &&
			<RenderProviderPrice providerItem={providerItem} />}
		{!!providerItem && !!providerItem.item &&
			<a className="cursor-pointer" onClick={() => openMarketStateItemUrl(providerItem.item)}>
				<div className="flex gap-1 items-center hover:text-blue-600">
					<span><RenderProviderPrice providerItem={providerItem} /></span>
					<span><RenderProviderIsMemberRatesIcon providerItem={providerItem} /></span>
					{isFlightProvider && !!dailyItem.airportCode &&
						<span className="ml-1 text-xs font-medium text-gray-400">({dailyItem.airportCode})</span>}
					<ArrowTopRightOnSquareIcon className="h-3 w-3" />
				</div>
			</a>}
	</td>;
};

const openMarketStateItemUrl = (item: MarketStateItem) => {
	MarketStateService.constructMarketStateItemUrl(item.id, [item.marketStateId])
		.then(url => {
			if (url !== null && url !== undefined && url !== '') window.open(url, '_blank', 'noopener,noreferrer');
		});
};

const RenderProviderPrice: React.FunctionComponent<{
	providerItem: ParityDailyProviderSnapshot | undefined
}> = ({ providerItem }) => {
	const render = useCallback(() => {
		if (providerItem) {
			if (providerItem.isAvailable || providerItem.isLos)
				return Utilities.formatPrice(providerItem.item.price);
			else if (providerItem.isNotAvailable)
				return <b>Not Available</b>;
			else if (providerItem.isSoldOut)
				return <b>Sold Out</b>;
		}
		return <b>Not Available</b>;
	}, [providerItem]);

	return render();
};

const RenderProviderIsMemberRatesIcon: React.FunctionComponent<{
	providerItem: ParityDailyProviderSnapshot | undefined
}> = ({ providerItem }) => {
	const render = useCallback(() => {
		if (providerItem && providerItem.item) {
			if ((providerItem.isAvailable || providerItem.isLos) && providerItem?.item?.filter?.hasMemberDiscount) {
				return <><img alt="Loyalty program" data-tooltip-id="loyalty-tooltip"
				            data-tooltip-content="Loyalty program" className="block h-2.5 w-2.5"
				            src={'/assets/img/icons/medal-solid.svg'} /><Tooltip id="loyalty-tooltip" /></>;
			}
		}
		return null;
	}, [providerItem]);

	return render();
};

const RenderProviderHeader: React.FunctionComponent<{
	provider: DataProvider,
	isFlightProvider?: boolean
}> = ({ provider, isFlightProvider }) => {
	const providerData = findProviderData(provider);
	return <th scope="col"
						 className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
		<div className="flex items-center gap-x-2">
			{providerData.icon}
			<span>{providerData.name}</span>
		</div>
		{isFlightProvider && <span className="block text-xs font-medium text-gray-400">Inc. Flight</span>}
	</th>;
};

const RenderOtaPriceDailyState: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	otaPriceStateSnapshot: OtaPriceStateSnapshot,
	brandItem?: ParityDailyProviderSnapshot,
	statDate: string,
	isPast: boolean
}> = ({ companyHotel, otaPriceStateSnapshot, brandItem, statDate, isPast }) => {

	const otaTextClass = isPast ? 'text-gray-500/20' : 'text-gray-500';

	if (otaPriceStateSnapshot.isEmpty) return <td className={`whitespace-nowrap px-3 py-4 text-sm ${otaTextClass}`}>
		<b>Not Available</b>
	</td>;

	const otaPriceDailyStateSnapshot = otaPriceStateSnapshot.dailyItems.find(daily => daily.statDate === statDate);

	if (!otaPriceDailyStateSnapshot || !otaPriceDailyStateSnapshot.isAvailable) return <td
		className={`whitespace-nowrap px-3 py-4 text-sm ${otaTextClass}`}>
		<b>Not Available</b>
	</td>;

	if (otaPriceDailyStateSnapshot.isSoldOut) return <td
		className={`whitespace-nowrap px-3 py-4 text-sm ${otaTextClass}`}>
		<b>Sold Out</b>
	</td>;

	const cheaperItem = otaPriceDailyStateSnapshot.items[0];
	let isCheaper = false;

	if (!!brandItem && brandItem.isAvailable) {
		isCheaper = (parseInt(cheaperItem.price.amount.toString()) * (1 + ((companyHotel.settings?.calendar?.parityDifferencePercentage ?? 0) / 100))) < parseInt(brandItem.item.price.amount.toString());
	}

	let textClass = isCheaper ? 'text-amber-500' : 'text-gray-500';
	textClass = isPast ? `${textClass}/20` : textClass;


	return <td className={`whitespace-nowrap px-3 py-4 text-sm border-x-[1px] border-gray-200`}>
		<div className="flex items-center flex-wrap gap-x-2">
			<span className={textClass}>{Utilities.formatPrice(cheaperItem.price)}</span>
			<span
				className={`inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium ${otaTextClass}`}>{cheaperItem.provider}</span>
			<span data-tooltip-id={`ota_${statDate}`}
						className={`text-xs font-medium ${otaTextClass} underline underline-offset-2 decoration-dotted decoration-gray-400 cursor-pointer`}>+{otaPriceDailyStateSnapshot.items.length - 1} more</span>
		</div>
		<Tooltip id={`ota_${statDate}`} className="shadow-lg !z-50 !p-0 !opacity-100" style={{ backgroundColor: 'white' }}
						 clickable
						 globalCloseEvents={{ scroll: true, clickOutsideAnchor: true }}
						 openOnClick>
			<div className="shadow ring-1 ring-black ring-opacity-5 max-h-56 overflow-y-auto">
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-300 bg-white">
							<thead>
							<tr className="divide-x divide-gray-200">
								<th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-900">Channel</th>
								<th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-900">Rate</th>
								<th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-900">Loss</th>
								<th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-900">Source</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
							{otaPriceDailyStateSnapshot.items.map((item, index) => {
								const diff = !!brandItem && brandItem.isAvailable && !item.isSoldOut ? item.price.amount - brandItem.item.price.amount : 0;
								const percentageDiff = !!brandItem && brandItem.isAvailable && !item.isSoldOut ? (diff / brandItem.item.price.amount) * 100 : 0;
								const textClass = diff < 0 ? 'text-amber-500' : 'text-gray-500';

								return <tr className="divide-x divide-gray-200" key={`ota_price_${statDate}_${index}`}>
									<td className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-900">
										<div className="flex items-center gap-x-2">
											<img className="block h-4 w-4"
													 src={`https://${item.iconUrl}`} alt={item.provider}
													 title={item.provider} />
											<a href={item.url} target="_blank">{item.provider}</a>
										</div>
									</td>
									<td
										className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">{Utilities.formatPrice(item.price)}</td>
									<td
										className={`whitespace-nowrap px-3 py-2 text-xs ${textClass}`}>
										{item.isSoldOut ? 'Sold Out' : <>{Utilities.formatAmount(diff, item.price.currency)} ({percentageDiff.toFixed(0)}%)</>}
									</td>
									<td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">Google</td>
								</tr>;
							})}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</Tooltip>
	</td>;
};

export default DashboardParityDetailsView;
